import { Injectable } from '@angular/core';
import {
  EditableWhenLeaf,
  EditableWhenNode,
  EditableWhenScope,
} from 'src/app/module/adjustment-grid/interface/adjustment.interface';
import { OrderFieldConfig, OrderFieldConfigMap } from 'src/app/module/order/interface/order-field.interface';
import {
  ExtendedPropertyApiDetail,
  ExtendedPropertyDetail,
  NumericConstraintConfig,
  PropertyAttribute,
  PropertyAttributeValue,
  PropertyFieldConfig,
  StructureAttribute,
  UnitAttribute,
} from 'src/app/module/property/interface/property.interface';
import { EndPoints } from '../api-service/api-endpoints';
import { ApiGatewayService } from '../api-service/api-gateway.service';
import { PropertyConfig } from '../config/property.config';
import { ExceptionConfig } from '../model/error.model';
import { Utilities } from './utilities';

export interface NumericConstraints {
  minValue: number | null;
  maxValue: number | null;
  allowNegative: boolean;
  decimalPrecision: number | null;
  rangeHintText: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  // settings: any = {};
  // enumeration: any = {};
  orderDetails: any = {};

  constructor(private apiGatewayService: ApiGatewayService) {}

  /**
   * Date-only ISO string -> "MM/DD/YYYY" using UTC parts (ngx-mask inputTransformFn).
   * Static so it stays correct when ngx-mask invokes it as a detached callback.
   */
  static formatUtcDateToInput(value: unknown): string {
    if (!value) return '';

    const date = new Date(value as string);
    if (isNaN(date.getTime())) return '';

    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${month}/${day}/${date.getUTCFullYear()}`;
  }

  /**
   * "MM/DD/YYYY" text -> UTC-midnight ISO string (ngx-mask outputTransformFn).
   * Static so it stays correct when ngx-mask invokes it as a detached callback.
   */
  static parseInputToUtcDateIso(value: string | number | undefined | null): string | null {
    if (!value) return null;

    const stringValue = value.toString();
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(stringValue)) return null;

    const [month, day, year] = stringValue.split('/');
    const utcDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return isNaN(utcDate.getTime()) ? null : utcDate.toISOString();
  }

  /**
   * Date, ISO string or the backend's "EEE MMM dd HH:mm:ss zzz yyyy" string -> UTC-midnight ISO string.
   * Material datepickers render anything that is not a Date or ISO string as empty.
   */
  static toUtcDateIso(value: unknown): string | null {
    if (!value) return null;

    const date = value instanceof Date ? value : new Date(value as string);
    if (isNaN(date.getTime())) return null;

    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())).toISOString();
  }

  /**
   * Get query params from the URL.
   * Use this method to read query params on page load.
   * Angular's ActivatedRoute.queryParams is not reliable on page load as it initially returns an empty object.
   * Use ActivatedRoute for all other cases.
   */
  public getQueryParams(): Record<string, string> {
    const queryParams: Record<string, string> = {};

    const url = window.location.href;
    const urlParams = new URLSearchParams(new URL(url).search);

    urlParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    return queryParams;
  }

  getOrderDetails() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.orderDetails;
  }

  getAllFormMetadata(reqBody: any, exptnConfig: ExceptionConfig) {
    const url = EndPoints.API_URLS.getAllFormMetadata + '?' + Utilities.encodeQueryParam(reqBody);
    return this.apiGatewayService.doGet(url, exptnConfig);
  }

  onFormControlValueChange(apiUrl: string, reqBody: any, exptnConfig: ExceptionConfig) {
    const url = (EndPoints.API_URLS as any)[apiUrl] + Utilities.encodeUrlParam(reqBody);
    return this.apiGatewayService.doGet(url, exptnConfig);
  }

  /** Utility fn to compare two javascript objects and return the json-path of modified values */
  // TODO: Return parent object path when old object value is null.
  getModifiedJsonPaths(oldObj: any, newObj: any, path: string = ''): string[] {
    const modifiedKeys: string[] = [];

    for (const key in newObj) {
      const newPath = path ? `${path}.${key}` : key;

      if (Array.isArray(newObj?.[key])) {
        if (!Array.isArray(oldObj?.[key])) {
          modifiedKeys.push(newPath);
        } else if (newObj?.[key].length !== oldObj?.[key].length) {
          modifiedKeys.push(newPath);
        } else {
          const isPrimitiveArray =
            newObj?.[key].every((item: any) => typeof item !== 'object' || item === null) &&
            oldObj?.[key].every((item: any) => typeof item !== 'object' || item === null);

          if (isPrimitiveArray) {
            for (let i = 0; i < newObj?.[key].length; i++) {
              if (newObj?.[key][i] !== oldObj?.[key][i]) {
                modifiedKeys.push(newPath);
                break;
              }
            }
          } else {
            for (let i = 0; i < newObj?.[key].length; i++) {
              const arrayPath = `${newPath}[${i}]`;

              if (typeof newObj?.[key][i] === 'object' && typeof oldObj?.[key][i] === 'object') {
                if (!this._isDeepEqual(newObj?.[key][i], oldObj?.[key][i])) {
                  modifiedKeys.push(...this.getModifiedJsonPaths(oldObj?.[key][i], newObj?.[key][i], arrayPath));
                }
              } else if (newObj?.[key][i] !== oldObj?.[key][i]) {
                modifiedKeys.push(arrayPath);
              }
            }
          }
        }
      } else if (
        typeof newObj?.[key] === 'object' &&
        !Array.isArray(newObj?.[key]) &&
        !(newObj?.[key] instanceof Date) &&
        newObj?.[key] != null
      ) {
        modifiedKeys.push(...this.getModifiedJsonPaths(oldObj?.[key], newObj?.[key], newPath));
      } else if (oldObj?.[key] !== newObj?.[key]) {
        modifiedKeys.push(newPath);
      }
    }

    return modifiedKeys;
  }

  /**
   * This function is used for formatting property data
   * @param data
   * @returns formatted Data
   */

  formatPropertyData(data: ExtendedPropertyApiDetail): ExtendedPropertyDetail;
  formatPropertyData(data: ExtendedPropertyApiDetail[]): ExtendedPropertyDetail[];
  formatPropertyData(
    data: ExtendedPropertyApiDetail | ExtendedPropertyApiDetail[]
  ): ExtendedPropertyDetail | ExtendedPropertyDetail[] {
    if (Array.isArray(data)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return data.map((item) => this.formatPropertyData(item));
    } else if (data != null && typeof data === 'object') {
      // const obj = data as Record<string, unknown>;
      const newObj: { [key in string]: unknown } = {};

      Object.keys(data).forEach((dataKey) => {
        let newKey = dataKey;
        if (['lr', 'pr', 'ir', 'ur', 'rr', 'ss', 'as', 'av', 'ue', 'ac', 'sv'].includes(dataKey)) {
          const propertyAbbreviation: Record<string, string> = {
            lr: 'listingRecord',
            pr: 'publicRecord',
            ir: 'inspectionRecord',
            ur: 'userRecord',
            rr: 'rentalRecord',
            ss: 'selectedSource',
            as: 'asyncDataStatus',
            av: 'adjustmentValue',
            ue: 'userEntered',
            ac: 'autoComputed',
            sv: 'selectedValue',
          };
          newKey = propertyAbbreviation[dataKey];
        }

        // Cast data to Record<string, unknown> for safe string indexing
        const dataAsRecord = data as Record<string, unknown>;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        newObj[newKey] = this.formatPropertyData(dataAsRecord[dataKey] as any);
      });
      return newObj;
    }

    return data;
  }

  getLength(value: unknown): number {
    let len = 0;
    if (typeof value === 'string') {
      len = value
        .replace(/<\/?p>/g, '')
        .trim()
        .split(/\s+/).length;
    } else if (Array.isArray(value)) {
      len = value.length;
    }
    return len;
  }

  /** Generating hash of an object */
  async generateHash(obj: any): Promise<string> {
    if (typeof obj == 'object') {
      obj = structuredClone(obj);
      obj = this.sortObject(obj);
    }

    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(obj));
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  /** Converting data to sorted data */
  sortObject(obj: any): any {
    if (obj === null || typeof obj !== 'object') return obj;

    if (obj instanceof Date) return obj.toISOString();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    if (Array.isArray(obj)) return obj.map((key) => this.sortObject(key));

    const sortedKeys = Object.keys(obj).sort();
    const result: any = {};

    sortedKeys.forEach((key) => {
      result[key] = this.sortObject(obj[key]);
    });

    return result;
  }

  /**
   * Resolves a field value from the correct nesting level of a property.
   * - UnitAttribute fields - structures[structureIndex].units[unitIndex][field]
   * - StructureAttribute fields - structures[structureIndex][field]
   * - All other fields - property[field]
   * Falls back to the flat property if the nested path doesn't exist.
   */
  resolvePropertyField(
    property: ExtendedPropertyDetail | null | undefined,
    fieldKey: PropertyAttribute,
    structureIndex = 0,
    unitIndex = 0
  ): PropertyAttributeValue | undefined {
    if (!property) return undefined;

    if ((PropertyConfig.unitAttributes as ReadonlyArray<string>).includes(fieldKey)) {
      return (
        property.structures?.[structureIndex]?.units?.[unitIndex]?.[fieldKey as UnitAttribute] ?? property[fieldKey]
      );
    }

    if ((PropertyConfig.structureAttributes as ReadonlyArray<string>).includes(fieldKey)) {
      return property.structures?.[structureIndex]?.[fieldKey as StructureAttribute] ?? property[fieldKey];
    }

    return property[fieldKey];
  }

  getRuleScope(node: EditableWhenNode | undefined): EditableWhenScope | undefined {
    return node?.scope;
  }

  /** Collects every field referenced by an editableWhen rule (recursively). */
  getEditableWhenFields<TField extends string>(node: EditableWhenNode<TField> | undefined): string[] {
    if (!node) return [];
    if ('and' in node) return node.and.flatMap((child) => this.getEditableWhenFields(child));
    if ('or' in node) return node.or.flatMap((child) => this.getEditableWhenFields(child));
    if ('nor' in node) return node.nor.flatMap((child) => this.getEditableWhenFields(child));
    if ('not' in node) return this.getEditableWhenFields(node.not);
    if (this._isEditableWhenLeaf(node)) return [node.field];
    return [];
  }

  evaluateEditableWhen(
    node: EditableWhenNode,
    property: ExtendedPropertyDetail,
    structureIndex = 0,
    unitIndex = 0
  ): boolean {
    return this.evaluateCondition(node, (field) => {
      const propertyAttributeValue = this.resolvePropertyField(property, field, structureIndex, unitIndex);
      return propertyAttributeValue?.selectedSource
        ? (propertyAttributeValue as any)[propertyAttributeValue.selectedSource]
        : undefined;
    });
  }

  /** Evaluates a rule tree, reading each leaf's value through `readValue`. A missing value never satisfies a leaf. */
  evaluateCondition<TField extends string>(
    node: EditableWhenNode<TField>,
    readValue: (field: TField) => unknown
  ): boolean {
    if ('and' in node) {
      return Array.isArray(node.and) && node.and.every((child) => this.evaluateCondition(child, readValue));
    }
    if ('or' in node) {
      return Array.isArray(node.or) && node.or.some((child) => this.evaluateCondition(child, readValue));
    }
    if ('nor' in node) {
      return Array.isArray(node.nor) && node.nor.every((child) => !this.evaluateCondition(child, readValue));
    }
    if ('not' in node) {
      const child = node.not;
      if (!child || Array.isArray(child) || typeof child !== 'object') return false;

      return !this.evaluateCondition(child, readValue);
    }
    if (this._isEditableWhenLeaf(node)) {
      return this._compareEditableWhenLeaf(node, readValue(node.field));
    }

    return false;
  }

  /**
   * Whether an order or scenario field's `enabledWhen` and `editableWhen` rules both allow editing. Order data
   * stores an unanswered field as '', so a blank value counts as missing and never satisfies a condition.
   * A field its own `enabledWhen` switches off counts as unanswered too, so a chain of dependent fields switches
   * off together even though each keeps its value.
   */
  isOrderFieldEditable(
    config: OrderFieldConfig | undefined,
    configs: OrderFieldConfigMap,
    readValue: (path: string) => unknown
  ): boolean {
    const visiting = new Set<string>();
    const isSwitchedOn = (path: string): boolean => {
      const rule = configs[path]?.enabledWhen;
      if (!rule || visiting.has(path)) return true;

      visiting.add(path);
      const switchedOn = this.evaluateCondition(rule, readAnswered);
      visiting.delete(path);
      return switchedOn;
    };
    const readAnswered = (path: string): unknown => {
      if (!isSwitchedOn(path)) return undefined;

      const value = readValue(path);
      return value === '' ? undefined : value;
    };

    return [config?.enabledWhen, config?.editableWhen].every(
      (rule) => !rule || this.evaluateCondition(rule, readAnswered)
    );
  }

  resolveNumericConstraints(
    fieldConfig: PropertyFieldConfig | undefined,
    isSubject: boolean,
    property?: ExtendedPropertyDetail | null,
    structureIndex = 0,
    unitIndex = 0
  ): NumericConstraints {
    let minValue = this._resolveNumericConstraintValue(fieldConfig?.minValue, property, structureIndex, unitIndex);
    let maxValue = this._resolveNumericConstraintValue(fieldConfig?.maxValue, property, structureIndex, unitIndex);

    if (isSubject) {
      if (fieldConfig?.subjectMinValue != null) {
        minValue = this._resolveNumericConstraintValue(
          fieldConfig.subjectMinValue,
          property,
          structureIndex,
          unitIndex
        );
      }
      if (fieldConfig?.subjectMaxValue != null) {
        maxValue = this._resolveNumericConstraintValue(
          fieldConfig.subjectMaxValue,
          property,
          structureIndex,
          unitIndex
        );
      }
    }

    const allowNegative = fieldConfig?.allowNegativeNumber ?? false;
    const decimalPrecision = fieldConfig?.decimalPrecision ?? null;

    const rangeHintText: string | null = this.resolveRangeText(minValue, maxValue);

    return { minValue, maxValue, allowNegative, decimalPrecision, rangeHintText };
  }

  resolveRangeText(minValue: number | null, maxValue: number | null): string | null {
    let rangeHintText: string | null = null;
    if (minValue != null && maxValue != null) {
      rangeHintText = `Min: ${minValue}, Max: ${maxValue}`;
    } else if (minValue != null) {
      rangeHintText = `Min: ${minValue}`;
    } else if (maxValue != null) {
      rangeHintText = `Max: ${maxValue}`;
    }

    return rangeHintText;
  }

  isValueOutOfRange(
    value: string | number | null | undefined,
    minValue: number | null,
    maxValue: number | null
  ): boolean {
    if (value == null || value === '') return false;
    const numericValue = typeof value === 'number' ? value : Number(value);
    if (isNaN(numericValue)) return false;
    if (minValue != null && numericValue < minValue) return true;
    if (maxValue != null && numericValue > maxValue) return true;
    return false;
  }

  /**
   * Checks whether field contains value as a case-insensitive substring.
   * Both sides must be strings, so a number, boolean or array value is never a match.
   * An empty value is also never a match, so a blank configured value does not
   * silently match every field.
   */
  containsIgnoreCase(haystack: unknown, needle: unknown): boolean {
    if (typeof haystack !== 'string' || typeof needle !== 'string') return false;
    if (needle === '') return false;

    return haystack.toLowerCase().includes(needle.toLowerCase());
  }

  private _resolveNumericConstraintValue(
    config: NumericConstraintConfig | undefined,
    property: ExtendedPropertyDetail | null | undefined,
    structureIndex: number,
    unitIndex: number
  ): number | null {
    if (config == null) return null;
    if (typeof config === 'number') return config;

    const fallback = config.default ?? null;
    if (!property) return fallback;

    const conditionMet = this.evaluateEditableWhen(config.when, property, structureIndex, unitIndex);

    return conditionMet ? (config.value ?? null) : fallback;
  }

  private _isEditableWhenLeaf<TField extends string>(node: EditableWhenNode<TField>): node is EditableWhenLeaf<TField> {
    return !('and' in node) && !('or' in node) && !('nor' in node) && !('not' in node) && 'field' in node;
  }

  private _compareEditableWhenLeaf<TField extends string>(leaf: EditableWhenLeaf<TField>, resolved: any): boolean {
    if (resolved == null) return false;

    switch (leaf.operator) {
      case 'eq':
        return resolved === leaf.value;
      case 'ne':
        return resolved !== leaf.value;
      case 'gt':
      case 'gte':
      case 'lt':
      case 'lte': {
        const left = Number(resolved);
        const right = Number(leaf.value);
        if (Number.isNaN(left) || Number.isNaN(right)) return false;
        if (leaf.operator === 'gt') return left > right;
        if (leaf.operator === 'gte') return left >= right;
        if (leaf.operator === 'lt') return left < right;

        return left <= right;
      }
      case 'in':
        return Array.isArray(leaf.value) && leaf.value.includes(resolved);
      case 'contains':
        return this.containsIgnoreCase(resolved, leaf.value);
      default:
        return false;
    }
  }

  private _isDeepEqual(objA: any, objB: any) {
    if (!objA && !objB) return true;

    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);

    if (keysA.length !== keysB.length) {
      return false;
    }

    for (const key of keysA) {
      if (typeof objA[key] === 'object' && typeof objB[key] === 'object') {
        if (!this._isDeepEqual(objA[key], objB[key])) {
          return false;
        }
      } else if (objA[key] !== objB[key]) {
        return false;
      }
    }

    return true;
  }
}
